import { IGlobalPromptSettings } from '@halo/interfaces/global-prompt-settings'
import { IUtmParams } from '@halo/interfaces/utm-params'
import { IFrequencyCapsConfig } from '@halo/interfaces/frequency-caps-config'
import { IDomainEcommConfig } from '@halo/interfaces/domain-ecomm-config'
import { IEngagementAttributionConfig } from '@halo/interfaces/engagement-attribution-config'
import { IAddedNotificationQueryParam } from '../interfaces/added-notification-query-param'
import {
    IAbstractNativeApnsSendIntegration,
    IAbstractNativeFcmSendIntegration,
    IWebApnsSendIntegration,
} from '@halo/interfaces/send-integrations'
import { NotificationActionType } from '@pushly/aqe/lib/enums/notification-action-type'
import { INotificationFieldSettings } from '@pushly/models/lib/interfaces'
import { getEnabledDeliveryChannels } from '@halo/_utils/domain'
import { Domain } from '@pushly/models/lib/structs'

export interface INotificationAction {
    id?: number
    type: NotificationActionType
    label: string
    ordinal: number
    landing_url?: string
    use_primary_landing_url: boolean
    display_meta?: any
}

export class DomainDto {
    public static fromApiResponse(fields: Partial<DomainDto>): DomainDto {
        const dto = new DomainDto()
        Object.assign(dto, fields)
        return dto
    }

    // Generic Details
    public id: number
    public name: string // DOMAIN NAME - eg: seaworld.com
    public display_name: string // DISPLAY NAME - eg: Seaworld
    public domain_key: string
    public account_name: string
    public account_id: number
    public status_id: number
    public sender_id: string
    public fcm_server_key: string
    public status: string
    public timezone: string
    public currency_code: string
    public currency?: {
        code: string
        name: string
    }
    public whitelist_domains: string[]
    public sdk_event_only_domains: string[]
    public account_flags: string[] = []
    public flags: string[] = []
    public display_meta: any
    public prod_domain_id: number
    public industry_id: number
    public industry?: {
        id: number
        name: string
    }

    // Server Keys
    public routing_key: string
    public collapse_key_type: string

    // Integration Keys
    public vapid_public_key: string
    public vapid_private_key: string
    public apns_configuration: IWebApnsSendIntegration
    public native_apns_configuration: IAbstractNativeApnsSendIntegration
    public native_fcm_configuration: IAbstractNativeFcmSendIntegration

    // Notification Settings
    public default_icon_url: string
    public default_badge_url: string
    public default_notification_ttl_seconds: number
    public notification_frequency_caps: any[]
    public delivery_throttle_max_seconds: number
    public duplicate_time_window: any
    public utm_params: IUtmParams
    public added_notification_query_params: { [key: string]: IAddedNotificationQueryParam }
    public media_quality?: number
    public landing_url_domain_whitelist?: string[]
    public default_notification_action_labels?: any
    public default_notification_actions: INotificationAction[] | null
    public notification_field_settings: INotificationFieldSettings | null

    // Prompt Settings
    public frequency_caps: IFrequencyCapsConfig
    public global_prompt_settings?: IGlobalPromptSettings

    // Campaign Settings
    public ecomm_config?: IDomainEcommConfig
    public engagement_attribution_config?: IEngagementAttributionConfig

    // Meta
    public readonly created_by: number
    public readonly created_at: Date
    public created_by_user_name: string
    public readonly updated_by: number
    public readonly updated_at: Date
    public updated_by_user_name: string

    // Page Analysis Config Settings
    public page_analysis_config_id: number

    // Demo Platform
    public is_demo: boolean = false
    public is_demo_template: boolean = false
    public source_demo_template_domain_id: number | null = null
    /** Write-only: pass on create to trigger the Clone Engine (not returned by the API) */
    public template_domain_id?: number

    public get activeChannels() {
        return getEnabledDeliveryChannels(Domain.from(this), true)
    }
}
